import React, { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightLong,
  faExchange,
  faPlus,
  faRandom,
  faRotate,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { faChevronsDown, faChevronsUp } from '@fortawesome/sharp-duotone-solid-svg-icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { addDays } from 'utils/utils';
import { onClickClose } from 'utils/responsive';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import RadioGroup from 'components/ui/RadioGroup/RadioGroup';
import DaterangePicker from 'components/ui/DaterangePicker/DaterangePicker';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import Button from 'components/ui/Button/Button';
import { Select } from 'ts/interfaces/Generic';
import ChoosePersons from './ChoosePersons';
import { useTranslation } from 'react-i18next';
import { faCalendar, faUser } from '@fortawesome/free-regular-svg-icons';
import { getFormattedCities } from 'utils/flight/flight';
import { cabinTypes, searchModes } from 'constants/flight';
import { SearchFlightModel } from 'ts/flight/classes/SearchFlightModel';
import * as idb from 'utils/shared/idbUrl';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { dateFormat } from 'utils/shared/utils';
import useRecaptcha from 'hooks/useRecaptcha';
import LocationInput from './LocationInput';
import { getDatesByCity, SearchDate } from 'services/flight';
import CalendarAlp from './CalendarAlp';
import { MainContext } from 'contexts/MainContext';
import { b2b } from 'constants/generic';

interface SearchFlightProps {
  loadingSearch: boolean;
  onClickStop: () => void;
  hideSecondRow?: boolean;
  destination?: {
    arrivalCode: string;
    arrivalDesc: string;
  };
}

const SearchFlight: FC<SearchFlightProps> = (props) => {
  const { t } = useTranslation();
  const [searchModel, setSearchModel] = useState(
    new SearchFlightModel(undefined, props.destination)
  );
  const addDaysCount = 3;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [additionalFrom, setAdditionalFrom] = useState('');
  const [additionalTo, setAdditionalTo] = useState('TLV');
  const [additionalFromLabel, setAdditionalFromLabel] = useState('');
  const [additionalToLabel, setAdditionalToLabel] = useState(t('tlv'));
  const prevStateMulti = useRef<SearchFlightModel>();
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const [multiDimensionalCities, setMultiDimensionalCities] = useState<Select[][]>([]);
  const [shortModeVisible] = useState(true);
  const [isShortMode, setIsShortMode] = useState(false);
  const { userSiteConfig, initSiteData } = useContext(MainContext);
  const b2bMode = b2b && userSiteConfig.base.professionalMode;
  const location = useLocation();
  const isResultPage = location.pathname.includes('results');
  const [reload, setReload] = useState(false);
  const { handleRecapcha, } = useRecaptcha();
  const [dateLoading, setDateLoading] = useState(false);
  const [alpDates, setAlpDates] = useState<SearchDate[]>([]);

  useEffect(() => {
    (async () => {
      const formattedCities = await getFormattedCities('', new AbortController());

      const grouped = formattedCities.reduce((acc: { [key: number]: Select[] }, item) => {
        return {
          ...acc,
          [item.data]: [...(acc[item.data] || []), item],
        };
      }, {});
      const val = Object.values(grouped).sort((a, b) => b[0].data - a[0].data);

      setMultiDimensionalCities(val);
    })();
  }, []);

  useEffect(() => {
    const depCode = urlParams.get('departureCode');
    const arrCode = urlParams.get('arrivalCode');
    const depDesc = urlParams.get('departureDesc');
    const arrDesc = urlParams.get('arrivalDesc');

    setIsSubmitted(false);

    if (depCode && arrCode && depDesc && arrDesc) {
      setSearchModel(new SearchFlightModel(urlParams));

      setAdditionalFrom(urlParams.get('additionalFrom') || '');
      setAdditionalTo(urlParams.get('additionalTo') || '');
      setAdditionalFromLabel(urlParams.get('additionalFromLabel') || '');
      setAdditionalToLabel(urlParams.get('additionalToLabel') || '');
    }

    if (props.destination) {
      setAdditionalFrom(props.destination.arrivalCode);
      setAdditionalFromLabel(props.destination.arrivalDesc);
    }

    if (!location.pathname.includes('results')) {
      setAdditionalFrom('');
      setAdditionalFromLabel('');
      setAdditionalTo('TLV');
      setAdditionalToLabel(t('tlv'));
      const newSearchModel = new SearchFlightModel();
      setSearchModel(newSearchModel);
      setReload(true);
      setTimeout(() => {
        setReload(false);
      }, 100);
    }
  }, [urlParams, location.pathname]); //eslint-disable-line

  const onClickSearch = async () => {
    setIsSubmitted(true);
    await idb.deletePageStateByPartialKey('flights');

    if (b2bMode && searchModel.searchType === '1') {
      if (!additionalFrom || !additionalTo) {
        return;
      }
    }

    if (
      searchModel.departureCode.split('|').every((code) => code) &&
      searchModel.arrivalCode.split('|').every((code) => code) &&
      searchModel.departureDate &&
      (searchModel.arrivalDate || ['0', '2'].includes(searchModel.searchType))
    ) {
      const searchParams = new URLSearchParams(searchModel as unknown as Record<string, string>);
      if (b2bMode && searchModel.searchType === '1') {
        searchParams.set('additionalFrom', additionalFrom);
        searchParams.set('additionalTo', additionalTo);
        searchParams.set('additionalFromLabel', additionalFromLabel);
        searchParams.set('additionalToLabel', additionalToLabel);
      } else {
        searchParams.delete('additionalFrom');
        searchParams.delete('additionalTo');
        searchParams.delete('additionalFromLabel');
        searchParams.delete('additionalToLabel');
      }

      const prevParams = urlParams.toString();

      const idbKey = 'flight-search' + searchParams;
      idb.deletePageState(idbKey);

      const element = document.querySelector('.search-wrapper') as HTMLElement;
      element?.classList.remove('visible-responsive');

      if (prevParams !== searchParams.toString()) {
        navigate({ pathname: '/flights/results', search: searchParams.toString() });
      } else {
        window.location.reload();
      }
    }
  };

  const onClickStop = () => {
    setIsSubmitted(false);
    props.onClickStop();
  };

  const onChangeFrom = (value: string, label: string, index: number) => {
    setAdditionalToLabel(label);
    setAdditionalTo(value);
    const departureCode = [...searchModel.departureCode.split('|')];
    departureCode[index] = value;
    const departureDesc = [...searchModel.departureDesc.split('|')];
    departureDesc[index] = label;
    setSearchModel({
      ...searchModel,
      departureCode: departureCode.join('|'),
      departureDesc: departureDesc.join('|'),
    });

    if (!searchModel.isGds) {
      getAlpDates();
    }
  };

  const onChangeTo = (value: string, label: string, index: number) => {
    setAdditionalFromLabel(label);
    setAdditionalFrom(value);
    const arrivalCode = [...searchModel.arrivalCode.split('|')];
    arrivalCode[index] = value;
    const arrivalDesc = [...searchModel.arrivalDesc.split('|')];
    arrivalDesc[index] = label;

    let departureCode = [...searchModel.departureCode.split('|')];
    let departureDesc = [...searchModel.departureDesc.split('|')];

    if (index + 1 < searchModel.departureCode.split('|').length) {
      departureCode[index + 1] = value;
      departureDesc[index + 1] = label;
    }

    setSearchModel({
      ...searchModel,
      arrivalCode: arrivalCode.join('|'),
      arrivalDesc: arrivalDesc.join('|'),
      departureCode: departureCode.join('|'),
      departureDesc: departureDesc.join('|'),
    });

    if (!searchModel.isGds) {
      getAlpDates(value);
    }
  };

  const onChangeType = (searchType: string) => {
    if (searchModel.searchType === '2') {
      prevStateMulti.current = searchModel;
    }

    if (searchType === '2') {
      const lastDate = searchModel.departureDate.split('|').slice(-1)[0];

      const nextDay = dateFormat(addDays(addDaysCount, new Date(lastDate)), 'yyyy-MM-dd');
      const nextDayTwo = dateFormat(addDays(addDaysCount * 2, new Date(lastDate)), 'yyyy-MM-dd');
      setSearchModel({
        ...searchModel,
        departureCode: searchModel.departureCode.concat('||'),
        departureDesc: searchModel.departureDesc.concat('||'),
        departureDate: searchModel.departureDate.concat('|', nextDay, '|', nextDayTwo),
        arrivalCode: searchModel.arrivalCode.concat('||'),
        arrivalDesc: searchModel.arrivalDesc.concat('||'),
        searchType,
      });
    } else {
      setSearchModel({
        ...searchModel,
        departureCode: searchModel.departureCode.split('|')[0],
        arrivalCode: searchModel.arrivalCode.split('|')[0],
        departureDate: searchModel.departureDate.split('|')[0],
        departureDesc: searchModel.departureDesc.split('|')[0],
        arrivalDesc: searchModel.arrivalDesc.split('|')[0],
        searchType,
      });
    }
  };

  const onClickX = (index: number) => {
    const departureCode = [...searchModel.departureCode.split('|')];
    departureCode.splice(index, 1);
    const arrivalCode = [...searchModel.arrivalCode.split('|')];
    arrivalCode.splice(index, 1);
    const departureDate = [...searchModel.departureDate.split('|')];
    departureDate.splice(index, 1);
    const departureDesc = [...searchModel.departureDesc.split('|')];
    departureDesc.splice(index, 1);
    const arrivalDesc = [...searchModel.arrivalDesc.split('|')];
    arrivalDesc.splice(index, 1);

    setSearchModel({
      ...searchModel,
      departureCode: departureCode.join('|'),
      arrivalCode: arrivalCode.join('|'),
      departureDate: departureDate.join('|'),
      departureDesc: departureDesc.join('|'),
      arrivalDesc: arrivalDesc.join('|'),
    });
  };

  const addAnotherFlight = () => {
    const lastDate = searchModel.departureDate.split('|').slice(-1)[0];
    const newDate = addDays(addDaysCount, new Date(lastDate));
    setSearchModel({
      ...searchModel,
      departureCode: searchModel.departureCode.concat('|'),
      departureDesc: searchModel.departureDesc.concat('|'),
      departureDate: searchModel.departureDate.concat('|', dateFormat(newDate, 'yyyy-MM-dd')),
      arrivalCode: searchModel.arrivalCode.concat('|'),
      arrivalDesc: searchModel.arrivalDesc.concat('|'),
    });
  };

  const onChangeDepartureDate = (newDepartureDate: Date, index: number) => {
    let newDepartureDateArr = [...searchModel.departureDate.split('|')];
    newDepartureDateArr[index] = dateFormat(newDepartureDate, 'yyyy-MM-dd');

    if (index < newDepartureDateArr.length - 1) {
      const nextDate = new Date(newDepartureDateArr[index + 1]);
      const activeDate = new Date(newDepartureDate);
      if (activeDate > nextDate) {
        for (let i = index + 1; i < newDepartureDateArr.length; i++) {
          const addDaysCount = (i - index) * 3;
          newDepartureDateArr[i] = dateFormat(addDays(addDaysCount, activeDate), 'yyyy-MM-dd');
        }
      }
    }

    setSearchModel({
      ...searchModel,
      departureDate: newDepartureDateArr.join('|'),
    });
  };

  const onChangeAdditionalFrom = (value: string, label: string) => {
    setAdditionalFrom(value);
    setAdditionalFromLabel(label);
  };

  const onChangeAdditionalTo = (value: string, label: string) => {
    setAdditionalTo(value);
    setAdditionalToLabel(label);
  };

  const departureCodeArr = searchModel.departureDesc.split('|');
  const arrivalCodeArr = searchModel.arrivalDesc.split('|');
  const departureDateArr = searchModel.departureDate.split('|');

  const onClickRotate = (index: number) => {
    const replaceElement = (arr: string, index: number, valueArr: string) => {
      const value = valueArr.split('|')[index];
      let result = arr.split('|');
      result[index] = value;
      return result.join('|');
    };

    setAdditionalFromLabel(additionalToLabel);
    setAdditionalToLabel(additionalFromLabel);
    setAdditionalFrom(additionalTo);
    setAdditionalTo(additionalFrom);

    const newDepartureCode = replaceElement(
      searchModel.departureCode,
      index,
      searchModel.arrivalCode
    );
    const newArrivalCode = replaceElement(
      searchModel.arrivalCode,
      index,
      searchModel.departureCode
    );
    const newDepartureDesc = replaceElement(
      searchModel.departureDesc,
      index,
      searchModel.arrivalDesc
    );
    const newArrivalDesc = replaceElement(
      searchModel.arrivalDesc,
      index,
      searchModel.departureDesc
    );
    setSearchModel({
      ...searchModel,
      departureCode: newDepartureCode,
      arrivalCode: newArrivalCode,
      departureDesc: newDepartureDesc,
      arrivalDesc: newArrivalDesc,
    });

    if (!searchModel.isGds) {
      getAlpDates(newArrivalCode);
    }
  };

  const getAlpDates = async (cityCode?: string, source?: string, isGds?: boolean) => {
    if (isGds) {
      return;
    } else if (isGds === undefined && searchModel.isGds) {
      return;
    }

    setDateLoading(true);
    setSearchModel((prev) => {
      return {
        ...prev,
        departureDate: '',
        arrivalDate: '',
      }
    });
    const token = await handleRecapcha();

    if (token) {
      const dates = await getDatesByCity({
        cityCode: cityCode || searchModel.arrivalCode,
        recaptchaToken: token,
        flightSourcesDefault: source || searchModel.flightSourcesDefault,
      });

      setAlpDates(dates || []);
    } else {
      setAlpDates([]);
    }

    setDateLoading(false);
  };

  const onClickGds = async (value: boolean) => {
    setSearchModel({
      ...searchModel,
      isGds: value,
    });

    if (value) {
      const newDepartureDate = dateFormat(addDays(14), 'yyyy-MM-dd', 'en');
      const newArrivalDate = dateFormat(addDays(21), 'yyyy-MM-dd', 'en');
      setSearchModel((prev) => ({ ...prev, departureDate: newDepartureDate, arrivalDate: newArrivalDate }));
    } else {
      getAlpDates(undefined, undefined, value);
    }
  };

  const onChangeSource = (value: string) => {
    setSearchModel({
      ...searchModel,
      flightSourcesDefault: value,
    });
    getAlpDates(searchModel.arrivalCode, value);
  };

  const onClickNewSearch = () => {
    navigate('/flights');
    idb.deletePageStateByPartialKey('/flight');
    setTimeout(() => {
      const latestPageUrl = localStorage.getItem('latestPageUrl');
      if (latestPageUrl) {
        const latestPageUrlObj = JSON.parse(latestPageUrl);
        localStorage.setItem('latestPageUrl', JSON.stringify({ ...latestPageUrlObj, flight: [] }));
      }
    }, 300);
  };

  if (reload) {
    return null;
  }

  return (
    <div className={'search-wrapper flight-search' + (b2bMode ? ' flight-s-b2b' : '')}>
      <div
        className={'search-inner' + (searchModel.searchType === '2' ? ' flight-search-inner' : '')}
      >
        <div className="results-responsive-header">
          <span className="title text-md">{t('search.search')}</span>
          <FontAwesomeIcon icon={faTimes as any} onClick={onClickClose} />
        </div>
        {departureCodeArr
          .filter((_, index) => (index === 0 && isShortMode) || !isShortMode)
          .map((item, index) => (
            <Fragment key={index}>
              <div className="flight-search-row-item">
                {isResultPage && (b2b || userSiteConfig.base.rememberModeEnabled) && (
                  <div className="search-element radius">
                    {index === 0 && (
                      <Button
                        title={t('home.new_search')}
                        onClick={onClickNewSearch}
                        className="btn btn-default"
                      />
                    )}
                  </div>
                )}
                {b2bMode && (
                  <div className="search-element mode">
                    {index === 0 && (
                      <span className="flight-search-b2b">
                        <span
                          className={searchModel.searchType === '0' ? 'active' : ''}
                          onClick={() => {
                            onChangeType('0');
                          }}
                        >
                          <FontAwesomeIcon icon={faArrowRightLong as any} />
                        </span>
                        <span
                          className={searchModel.searchType === '1' ? 'active' : ''}
                          onClick={() => {
                            onChangeType('1');
                          }}
                        >
                          <FontAwesomeIcon icon={faExchange as any} />
                        </span>
                        <span
                          className={searchModel.searchType === '2' ? 'active' : ''}
                          onClick={() => {
                            onChangeType('2');
                          }}
                        >
                          <FontAwesomeIcon icon={faRandom as any} />
                        </span>
                      </span>
                    )}
                    {index === 1 && (
                      <SelectCustom
                        options={cabinTypes}
                        value={searchModel.cabineType}
                        onChange={(cabineType) => setSearchModel({ ...searchModel, cabineType })}
                        highInput={true}
                        needToTranslate={true}
                        isDropdown={true}
                      />
                    )}
                    {index === 2 &&
                      searchModel.searchType === '2' &&
                      searchModel.departureCode.split('|').length < 5 && (
                        <div className="flight-search-add ph-10">
                          <span onClick={addAnotherFlight}>
                            <FontAwesomeIcon icon={faPlus as any} />{' '}
                            {t('flight.add_another_flight')}
                          </span>
                        </div>
                      )}
                  </div>
                )}
                <LocationInput
                  value={item}
                  onChange={(val, label) => onChangeFrom(val, label, index)}
                  placeholder={t('flight.from')}
                  isInvalid={isSubmitted && !item}
                  defaultOptions={multiDimensionalCities}
                />
                <div className="search-element flight-search-rotate">
                  <FontAwesomeIcon icon={faRotate as any} onClick={() => onClickRotate(index)} />
                </div>
                <LocationInput
                  value={arrivalCodeArr[index]}
                  onChange={(val, label) => onChangeTo(val, label, index)}
                  placeholder={t('flight.to')}
                  isInvalid={isSubmitted && !arrivalCodeArr[index]}
                  defaultOptions={multiDimensionalCities}
                />
                {b2bMode && (
                  <div className="ml-a search-element small">
                    {index === 0 && (
                      <div className="persons-wrapper">
                        <ChoosePersons
                          searchModel={searchModel}
                          onChange={(value) => setSearchModel(value)}
                          movablePlaceholder={t('flight.choose_persons')}
                          movablePlaceholderIcon={faUser}
                        />
                      </div>
                    )}
                    {index === 2 && (
                      <Checkbox
                        label={t('flight.direct_only')}
                        checked={searchModel.directFlight}
                        onChange={(directFlight) =>
                          setSearchModel({ ...searchModel, directFlight })
                        }
                      />
                    )}
                  </div>
                )}
                {['0', '2'].includes(searchModel.searchType) && (
                  <div
                    className="search-element date"
                    style={{ paddingInlineEnd: index === 0 ? 3 : 30 }}
                  >
                    {(searchModel.isGds || searchModel.searchType === '2') &&
                      <FormGroupDatepicker
                        value={new Date(departureDateArr[index])}
                        onChange={(date) => onChangeDepartureDate(date, index)}
                        label={t('flight.date')}
                        minDate={index >= 1 ? new Date(departureDateArr[index - 1]) : new Date()}
                        longFormat={true}
                        movablePlaceholder={b2bMode ? undefined : t('flight.date')}
                        movablePlaceholderIcon={b2bMode ? undefined : faCalendar}
                        vertical={b2bMode ? true : false}
                      />}
                    {!searchModel.isGds && searchModel.searchType === '0' &&
                      <div className="flight-search-alp-calendar">
                        <CalendarAlp
                          dates={alpDates}
                          searchType={searchModel.searchType}
                          departureDate={searchModel.departureDate}
                          onChangeStartDate={(startDate) => setSearchModel({ ...searchModel, departureDate: dateFormat(startDate, 'yyyy-MM-dd', 'en') })}
                          onChangeEndDate={() => { }}
                          flightSourcesDefault={searchModel.flightSourcesDefault}
                          arrivalCode={searchModel.arrivalCode}
                        />
                      </div>
                    }
                  </div>
                )}
                {index >= 2 && (
                  <span className="flight-search-remove">
                    <FontAwesomeIcon icon={faTimes as any} onClick={() => onClickX(index)} />
                  </span>
                )}
                {searchModel.searchType === '1' && (
                  <div className={'search-element date'}>

                    {searchModel.isGds && <DaterangePicker
                      appendToRight={true}
                      value={{
                        startDate: searchModel.departureDate,
                        endDate: searchModel.arrivalDate,
                      }}
                      onChange={({ startDate: departureDate, endDate: arrivalDate }) =>
                        setSearchModel({
                          ...searchModel,
                          departureDate: departureDate,
                          arrivalDate: arrivalDate,
                        })
                      }
                      movablePlaceholder={b2bMode ? undefined : t('flight.date')}
                      movablePlaceholderIcon={b2bMode ? undefined : faCalendar}
                      maxDateRange={-1}
                      scrollOnFocusMobile
                      vertical={b2bMode ? true : false}
                      showNights={b2bMode ? true : false}
                    />}

                    {!searchModel.isGds && (
                      <div className="flight-search-alp-calendar">
                        <CalendarAlp
                          dates={alpDates}
                          searchType={searchModel.searchType}
                          departureDate={searchModel.departureDate}
                          arrivalDate={searchModel.arrivalDate}
                          flightSourcesDefault={searchModel.flightSourcesDefault}
                          isLoading={dateLoading}
                          onChangeStartDate={(startDate) => setSearchModel({ ...searchModel, departureDate: dateFormat(startDate, 'yyyy-MM-dd', 'en') })}
                          onChangeEndDate={(endDate) => setSearchModel({ ...searchModel, arrivalDate: dateFormat(endDate, 'yyyy-MM-dd', 'en') })}
                          arrivalCode={searchModel.arrivalCode}
                        />
                      </div>
                    )}
                  </div>
                )}
                {shortModeVisible && index === 0 && (
                  <div className="search-button hide-laptop-sm" style={{ width: 'auto' }}>
                    <button
                      style={{ width: 28, padding: '0 3px' }}
                      className="btn btn-default"
                      onClick={() => setIsShortMode((prev) => !prev)}
                    >
                      <FontAwesomeIcon
                        icon={(isShortMode ? faChevronsDown : faChevronsUp) as any}
                      />
                    </button>
                  </div>
                )}
                {index === 0 && (
                  <div className="search-button">
                    {!props.loadingSearch && (
                      <button className="btn btn-primary btn-go" onClick={onClickSearch}>
                        GO
                      </button>
                    )}
                    {props.loadingSearch && (
                      <button className="btn btn-active" onClick={onClickStop}>
                        <FontAwesomeIcon icon={faTimes as any} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {!isShortMode && index === departureCodeArr.length - 1 && (
                <div className="flight-search-row-item">
                  {searchModel.searchType !== '2' && (
                    <div className="search-element small small-150">
                      <SelectCustom
                        options={cabinTypes}
                        value={searchModel.cabineType}
                        onChange={(cabineType) => setSearchModel({ ...searchModel, cabineType })}
                        highInput={true}
                        needToTranslate={true}
                        isDropdown={true}
                      />
                    </div>
                  )}
                  {searchModel.searchType === '1' && b2bMode && (
                    <Fragment>
                      <LocationInput
                        value={additionalFromLabel}
                        onChange={onChangeAdditionalFrom}
                        placeholder={t('flight.from')}
                        isInvalid={isSubmitted && !additionalFrom}
                        defaultOptions={multiDimensionalCities}
                      />
                      <div className="search-element flight-search-rotate">
                        <FontAwesomeIcon
                          icon={faRotate as any}
                          style={{ visibility: 'hidden', cursor: 'default' }}
                        />
                      </div>
                      <LocationInput
                        value={additionalToLabel}
                        onChange={onChangeAdditionalTo}
                        placeholder={t('flight.to')}
                        isInvalid={isSubmitted && !additionalToLabel}
                        defaultOptions={multiDimensionalCities}
                      />
                    </Fragment>
                  )}
                  {!b2bMode && (
                    <div className="search-element small mr-10">
                      <div className="persons-wrapper">
                        <ChoosePersons
                          searchModel={searchModel}
                          onChange={(value) => setSearchModel(value)}
                          movablePlaceholder={t('flight.choose_persons')}
                          movablePlaceholderIcon={faUser}
                        />
                      </div>
                    </div>
                  )}
                  {!b2bMode && (
                    <div className="flex-row">
                      <RadioGroup
                        options={searchModes}
                        value={searchModel.searchType}
                        onChange={onChangeType}
                        needToTranslate={true}
                      />

                      {searchModel.searchType === '2' &&
                        searchModel.departureCode.split('|').length < 5 && (
                          <div className="ph-10 cur-poi">
                            <span onClick={addAnotherFlight}>
                              <FontAwesomeIcon icon={faPlus as any} />{' '}
                              {t('flight.add_another_flight')}
                            </span>
                          </div>
                        )}
                    </div>
                  )}

                  {b2bMode &&
                    searchModel.searchType === '2' &&
                    searchModel.departureCode.split('|').length < 3 && (
                      <div className="ph-10 cur-poi">
                        <span onClick={addAnotherFlight}>
                          <FontAwesomeIcon icon={faPlus as any} /> {t('flight.add_another_flight')}
                        </span>
                      </div>
                    )}

                  {(!b2bMode ||
                    searchModel.searchType === '0' ||
                    searchModel.searchType === '1') && (

                      <Fragment>
                        <div className="flight-search-options">
                          {initSiteData?.other?.baggageSearchVisible === 'true' && (
                            <div className="search-element mode">
                              <Checkbox
                                label={t('flight.baggage')}
                                checked={searchModel.numberOfBags > 0}
                                onChange={(numberOfBags) =>
                                  setSearchModel({
                                    ...searchModel,
                                    numberOfBags: numberOfBags ? 1 : 0,
                                  })
                                }
                              />
                            </div>
                          )}

                          <div className="search-element mode">
                            <Checkbox
                              label={t('flight.direct_only')}
                              checked={searchModel.directFlight}
                              onChange={(directFlight) =>
                                setSearchModel({ ...searchModel, directFlight })
                              }
                            />
                          </div>

                          {b2b && (
                            <Fragment>
                              <div className="search-element mode">
                                <Checkbox
                                  label={'GDS'}
                                  checked={searchModel.isGds}
                                  onChange={onClickGds}
                                />
                              </div>

                              <div className="search-element mode">
                                <Checkbox
                                  label={'NDC'}
                                  checked={searchModel.isNDC}
                                  onChange={(isNDC) => setSearchModel({ ...searchModel, isNDC })}
                                />
                              </div>

                              <div className="search-element mode">
                                <Checkbox
                                  label={'Low Cost'}
                                  checked={searchModel.isLowCost}
                                  onChange={(isLowCost) =>
                                    setSearchModel({ ...searchModel, isLowCost })
                                  }
                                />
                              </div>

                              <div className="search-element mode flight-search-source">
                                <SelectCustom
                                  options={[
                                    { value: '', label: 'NONE' },
                                    { value: 'ALP', label: 'ALP' },
                                    { value: 'ABOX', label: 'ABOX' },
                                  ]}
                                  value={searchModel.flightSourcesDefault}
                                  onChange={onChangeSource}
                                />
                              </div>
                            </Fragment>
                          )}
                        </div>
                      </Fragment>

                    )}
                </div>
              )}
            </Fragment>
          ))}
      </div>
    </div>
  );
};
export default withErrorBoundary(SearchFlight, 'SearchFlight');
